import { ClientOnly, Link } from "@tanstack/react-router";
import { type LucideIcon, WandSparklesIcon } from "lucide-react";
import { Suspense } from "react";

import { type ButtonProps } from "@/web/components/primitives/button";
import { DropdownMenuItem } from "@/web/components/primitives/dropdown-menu";
import { LinkButton } from "@/web/components/styled/LinkButton";
import { useFeatureFlag } from "@/web/lib/featureFlags";
import { cn } from "@/web/lib/styling";

type AnalyzeLeaseButtonProps = {
  className?: string;
  /** Visible button text. Defaults to "Analyze my lease". */
  label?: string;
  /** Leading icon. Defaults to the wand-sparkles. */
  icon?: LucideIcon;
  size?: ButtonProps["size"];
  color?: ButtonProps["color"];
};

/**
 * CTA that sends a visitor to the anonymous `/lease-grader` chat. Rendered ONLY
 * when the `LEASE_GRADER` feature flag is on — otherwise nothing paints, so the
 * button never hints at a hidden route.
 *
 * `useFeatureFlag` reads a SpacetimeDB table via `useSuspenseQuery`, which needs a
 * live client connection, so this is wrapped in `<ClientOnly>` (+ a `<Suspense>`)
 * — the homepage hero SSRs, and a bare suspense read there would hang/throw. The
 * flag being off or still loading renders nothing (a CTA that pops in is fine for
 * an optional button; there's no layout to reserve).
 */
export function AnalyzeLeaseButton(props: AnalyzeLeaseButtonProps) {
  return (
    <ClientOnly>
      <Suspense>
        <AnalyzeLeaseButtonInner {...props} />
      </Suspense>
    </ClientOnly>
  );
}

function AnalyzeLeaseButtonInner({
  className,
  label = "Analyze your lease",
  icon: Icon = WandSparklesIcon,
  size = "sm",
  color = "red",
}: AnalyzeLeaseButtonProps) {
  const enabled = useFeatureFlag("LEASE_GRADER");
  if (!enabled) return null;

  return (
    <LinkButton
      to="/lease-grader"
      variant="solid"
      color={color}
      size={size}
      className={cn("font-withus-sans rounded-none", className)}
    >
      <Icon className="size-4" />
      {label}
    </LinkButton>
  );
}

/**
 * The same CTA as a mobile-menu row (for the navbar hamburger). Flag-gated exactly
 * like {@link AnalyzeLeaseButton}; renders nothing when the flag is off. Wrapped in
 * `<ClientOnly>`/`<Suspense>` for the same SSR/connection reason. The navbar's
 * mobile menu supplies the surrounding group and divider.
 */
export function AnalyzeLeaseMenuItem() {
  return (
    <ClientOnly>
      <Suspense>
        <AnalyzeLeaseMenuItemInner />
      </Suspense>
    </ClientOnly>
  );
}

function AnalyzeLeaseMenuItemInner() {
  const enabled = useFeatureFlag("LEASE_GRADER");
  if (!enabled) return null;

  return (
    <DropdownMenuItem asChild>
      <Link to="/lease-grader" className="w-full cursor-pointer">
        <WandSparklesIcon className="size-4" />
        Analyze your lease
      </Link>
    </DropdownMenuItem>
  );
}
